<script setup lang="ts">
  import { useGetAccount } from "@/api/account/account"
  import { buildAddressLine1 } from "@/lib/shared/addressHelper"
  import { openWebchat } from "@/services/WebchatService"
  import { computed } from "vue"
  import { Button } from "@/components/ui/button"
  import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
  } from "@/components/ui/dialog"
  import { Link } from "@/components/ui/link"

  const props = defineProps<{
    accountId: number
  }>()

  const accountQuery = useGetAccount(computed(() => props.accountId))
  const currentHome = computed(() => {
    const address = accountQuery.data.value?.supplyAddress
    return address ? buildAddressLine1(address) : "your current home"
  })

  const cancelEmailHref = "mailto:help@so.energy?subject=Cancel%20my%20move"
</script>

<template>
  <div>
    <Dialog>
      <DialogTrigger as-child>
        <button
          type="button"
          class="link"
          data-testid="move-with-us-cancel-trigger"
        >
          Cancel your move with us
        </button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Need to cancel your move?</DialogTitle>
        </DialogHeader>

        <div class="space-y-4">
          <p>
            You can't cancel your move online, but our team can sort it for you.
          </p>
          <p>
            Get in touch and we'll keep your account open at {{ currentHome }}.
          </p>
          <p>
            Email us at
            <Link class="link" to="mailto:help@so.energy">help@so.energy</Link>
            with “Cancel my move” in the subject line. You can also
            <button type="button" class="link" @click="openWebchat">
              chat online
            </button>
            with our team (Monday to Friday, 9am to 5pm).
          </p>
        </div>

        <DialogFooter>
          <DialogClose as-child>
            <Button variant="outline" data-testid="move-with-us-cancel-keep">
              Keep my move
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            as="a"
            :href="cancelEmailHref"
            icon="Email"
            icon-position="left"
            data-testid="move-with-us-cancel-email"
          >
            Send an email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
